import React from 'react';
import { IHasTestData } from '../../../../../ui-toolkit/test-utilities/IHasTestData';
import { EditPartyForm } from './EditPartyForm';
import { usePartySelector } from 'state/orderOverview/selectors/usePartySelector';
import { eApplicationActions, DispatchApplicationAction, IPartySchema } from 'state';
import { savePartyAsync } from 'state/orderOverview/actions/PartyActions/savePartyAsync';
import { PartyEntityRow } from './PartyEntityRow';
import { EditEntityDataEntry } from './EditEntityDataEntry';
import { PartyPersonRow } from './PartyPersonRow';
import { EditPersonDataEntry } from './EditPersonDataEntry';
import { Field } from 'formik';
import { useAppDispatch } from 'store/store';
import { loadOrderAsyncAction } from 'store/order/batchedDispatches';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from '../../../../../utilities/Assertions';
import { PartyNotificationMessage } from './PartyNotificationMessage';
import { ePartyPurpose } from '../../../../../ui-toolkit/enums/ePartyPurpose';
import { ePartyType } from '../../../../../ui-toolkit/enums/ePartyType';
import { FullName, IParty } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { rightChevron } from '../../../../../utilities/order/magicStrings';
import { IFieldChildProps } from '../../../../../ui-toolkit/utilities/formikUtilities';

export interface IPartyRowContainerProps extends IHasTestData {
  party: IParty;
  isNew: boolean;
}

const getEditByPurpose = (party: IParty, dispatch: DispatchApplicationAction) => {
  switch (party.purpose) {
    case ePartyPurpose.borrower:
    case ePartyPurpose.buyer:
    case ePartyPurpose.seller:
      return {
        onClick: () => dispatch({ type: eApplicationActions.StartPartyEdit, payload: party }),
        text: `${FullName(party)} ${rightChevron}`,
      };
  }
  return undefined;
};

const partiesThatHaveExtraWarning = [ePartyPurpose.borrower, ePartyPurpose.buyer, ePartyPurpose.seller];

export const PartyRowContainer: React.FC<IPartyRowContainerProps> = ({ party, isNew, 'data-test-id': dataTestID }) => {
  const [{ isUser, isEditing, canDelete, canEdit }, dispatch] = usePartySelector(party, isNew);
  const dispatchRedux = useAppDispatch();
  const { data: order } = useSelectCurrentOrder();
  const orderId = order.transaction.id;
  assertDefined(orderId);

  const onSubmit = savePartyAsync({
    isPartyNew: isNew,
    orderId,
    dispatch,
    dispatchRedux,
    hasExtraSuccessWarning: partiesThatHaveExtraWarning.some((t) => t === party.purpose),
    onSuccess: async () => {
      await dispatchRedux(loadOrderAsyncAction(order.transaction.id!));
      dispatch({ type: eApplicationActions.FinishPartyChanges });
    },
  });
  const navDeleteStart = canDelete
    ? { onClick: () => dispatch({ type: eApplicationActions.StartPartyDelete, payload: party }), text: `` }
    : undefined;
  const navEditStart = canEdit ? getEditByPurpose(party, dispatch) : undefined;

  return isEditing ? (
    <EditPartyForm
      party={party}
      isNew={isNew}
      onEditEndOnClick={() => dispatch({ type: eApplicationActions.FinishPartyChanges })}
      navDeleteStart={navDeleteStart}
      onSubmit={onSubmit}
      notification={<PartyNotificationMessage isNew={isNew} partyPurpose={party.purpose} />}
    >
      <Field name="type">
        {({ field }: IFieldChildProps<ePartyType, IPartySchema>) =>
          field.value === ePartyType.person ? (
            <EditPersonDataEntry isNew={isNew} />
          ) : (
            <EditEntityDataEntry isNew={isNew} />
          )
        }
      </Field>
    </EditPartyForm>
  ) : (
    <>
      {party.type === ePartyType.person ? (
        <PartyPersonRow data-test-id={dataTestID} person={party} isUser={isUser} navEditStart={navEditStart} />
      ) : (
        <PartyEntityRow data-test-id={dataTestID} entity={party} isUser={isUser} navEditStart={navEditStart} />
      )}
    </>
  );
};
